/**
 * Cross-tab contract between a manual page (/home/docs/...) and the app shell.
 *
 * The manual is read in its own tab so it stays on screen while the reader follows it; the "open in
 * app" buttons therefore must not navigate the manual's own tab. They post on this channel instead,
 * and whichever app tab is already open moves to the route.
 */
export const DOCS_NAVIGATE_CHANNEL = 'pc-docs-navigate';

/**
 * Heartbeat every app tab writes while it is showing the app rather than a manual, so a manual can
 * tell SYNCHRONOUSLY — inside the click handler — whether anyone is out there to receive a request.
 *
 * Waiting for an ack instead would mean deciding a beat later, and anything a page does after the
 * user's gesture has ended can no longer open a tab: Chrome's popup blocker eats it silently.
 */
export const DOCS_APP_TAB_HEARTBEAT_KEY = 'pc-docs-app-tab-alive';
export const DOCS_APP_TAB_HEARTBEAT_INTERVAL_MS = 2000;
/** A heartbeat older than this belongs to a tab that has been closed. */
export const DOCS_APP_TAB_STALE_MS = 6000;

/**
 * The heartbeat carries which tab wrote it, so a tab can discount its own: opening a manual in the
 * tab you were just using leaves that tab's own heartbeat behind, and without the tab id the manual
 * would hand its request to a listener that is itself and the click would do nothing.
 */
export function formatHeartbeat(tabId: string, at: number): string {
  return `${tabId}:${at}`;
}

export function parseHeartbeat(raw: string | null): { tabId: string; at: number } | null {
  const separator = raw?.lastIndexOf(':') ?? -1;
  if (raw === null || separator < 1) return null;

  const at = Number(raw.slice(separator + 1));
  return Number.isFinite(at) ? { tabId: raw.slice(0, separator), at } : null;
}

/**
 * Identifies this browsing context for the lifetime of the document.
 *
 * BroadcastChannel excludes only the channel OBJECT that posted, not the tab: the manual's sender
 * and the app shell's listener are two objects in the same tab, so without this the docs tab
 * answers its own request and navigates itself away from the manual instead of opening a tab.
 */
export const DOCS_TAB_ID = `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export interface IDocsNavigateRequest {
  type: 'docs-navigate-request';
  /** In-app route, e.g. `/home/assistant-tools/latereply`. */
  route: string;
  /** Distinguishes one request from the next in logs and tests. */
  requestId: string;
  /** The tab that asked — listeners in that same tab must ignore it. */
  senderTabId: string;
}

export type DocsNavigateMessage = IDocsNavigateRequest;

export function isDocsNavigateRequest(message: unknown): message is IDocsNavigateRequest {
  return typeof message === 'object' && message !== null && (message as IDocsNavigateRequest).type === 'docs-navigate-request';
}
